import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

export default function DeleteAccount() {
  const [email,    setEmail]    = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [sent,     setSent]     = useState(false)

  useEffect(() => { document.title = 'Delete account — PantryIO' }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/request-account-deletion', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: email.trim() }),
      })

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}))
        setError(payload.error || 'Something went wrong. Please try again.')
        return
      }

      setSent(true)
    } catch {
      setError('Network error. Please check your connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">

      {/* Header */}
      <header className="px-6 py-4 border-b border-border-sand">
        <Link to="/" className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-[9px] bg-forest text-forest-ink font-serif italic text-[16px] font-semibold leading-none">
            P
          </span>
          <span className="font-serif text-[15px] font-medium text-ink italic">Pantry</span>
        </Link>
      </header>

      <main className="flex-1 px-6 py-8 sm:py-12">
        <div className="max-w-[560px] mx-auto">
          <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-critical mb-2">
            Account
          </div>
          <h1 className="font-serif text-[30px] sm:text-[36px] font-normal leading-[1.1] tracking-[-0.015em] text-ink">
            Delete your <em className="italic">account</em>
          </h1>
          <p className="font-serif text-[15px] italic text-ink-muted mt-2 leading-[1.5]">
            Permanently delete your PantryIO account and all associated data.
          </p>

          {/* Form / success card */}
          <article className="bg-paper border border-border-sand rounded-2xl p-6 sm:p-8 mt-8">
            {sent ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
                  Sent
                </div>
                <h2 className="font-serif text-[22px] text-ink leading-[1.2] tracking-[-0.01em] mb-3">
                  Check your <em className="italic">email</em>.
                </h2>
                <p className="font-serif text-[15px] text-ink leading-[1.6]">
                  If an account exists with that address, we've sent you a confirmation link. The link expires in 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="delete-email" className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
                    Email address
                  </label>
                  <input
                    id="delete-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
                  />
                </div>

                {error && (
                  <p className="rounded-[12px] bg-critical-bg border border-critical/20 text-critical text-[13px] p-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-50 transition mt-2"
                >
                  {loading ? 'Sending…' : 'Delete account'}
                </button>
              </form>
            )}
          </article>

          {/* What gets deleted */}
          <section className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-3">
              What gets deleted
            </div>
            <ul className="list-disc pl-5 font-serif text-[15px] text-ink leading-[1.65] space-y-1.5">
              <li>Your account and login credentials</li>
              <li>Your personal items, recipes, shopping lists, and scan history</li>
              <li>Your subscription will be canceled</li>
              <li>Your data will be removed within 24 hours</li>
            </ul>
          </section>

          {/* What's preserved */}
          <section className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-3">
              What's preserved
            </div>
            <ul className="list-disc pl-5 font-serif text-[15px] text-ink leading-[1.65] space-y-1.5">
              <li>If you share a household, the household and items added by other members stay intact</li>
              <li>Stripe payment receipts are kept for tax/legal reasons (no card details — Stripe handles all payment data)</li>
            </ul>
          </section>

          {/* Footer link */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="font-serif text-[14px] italic text-ink-muted underline-offset-4 hover:text-ink hover:underline"
            >
              Cancel — go back to PantryIO
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
